import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, hasCategory, HardwareGlyph, strikeEdgeX} from "./HardwareGlyph";

// an exit device lever trim seen from the pull side: the lever on its rose at the hub
// (the spindle, in line with the device's chassis on the other face), reaching from the
// strike side toward the hinge edge. An escutcheon trim adds the tall rounded plate
// behind it, and a keyed function (storeroom / classroom) the cylinder above the lever.
// Sizes are typical catalogue values (scene units = mm).
const PLATE_WIDTH = 2.75 * inches;
const PLATE_HEIGHT = 10.5 * inches;
const PLATE_RADIUS = 1 * inches;
const PLATE_ABOVE_HUB = 6.5 * inches;      // plate top above the spindle
const CYLINDER_ABOVE_HUB = 3.75 * inches;
const CYLINDER_RADIUS = 0.625 * inches;
const ROSE_RADIUS = 1.125 * inches;
const LEVER_LENGTH = 4.5 * inches;
const LEVER_THICKNESS = 0.75 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

export const leverTrim: HardwareGlyph = {
    id: "leverTrim",
    targets: ["door"],
    matches: item => hasCategory(item, "Lever Trims"),
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const cx = ctx.hub.x;
        const cy = ctx.hub.y;
        // `dir` points from the strike edge toward the hinge edge: the lever reaches that way.
        const dir = strikeEdgeX(ctx).dir;

        const escutcheon = hasCategory(ctx.item, "Classroom Escutcheon Trim", "Dummy Escutcheon Trim", "Passage Escutcheon Trim", "Storeroom Escutcheon Trim");
        const keyed = hasCategory(ctx.item, "Classroom Escutcheon Trim", "Storeroom Escutcheon Trim", "Classroom Lever Trim", "Storeroom Lever Trim");

        const plateTop = cy - PLATE_ABOVE_HUB;
        if(escutcheon) {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(color, alpha * 0.2);
            g.drawRoundedRect(cx - PLATE_WIDTH / 2, plateTop, PLATE_WIDTH, PLATE_HEIGHT, PLATE_RADIUS);
            g.endFill();
        }

        if(keyed) {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(color, alpha * 0.35);
            g.drawCircle(cx, cy - CYLINDER_ABOVE_HUB, CYLINDER_RADIUS);
            g.endFill();
        }

        // lever: solid, from the spindle toward the hinge edge, rounded at the grip end.
        const leverEnd = cx + dir * LEVER_LENGTH;
        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRoundedRect(Math.min(cx, leverEnd), cy - LEVER_THICKNESS / 2, LEVER_LENGTH, LEVER_THICKNESS, LEVER_THICKNESS / 2);
        g.endFill();

        // rose at the hub so the placement point stays visible.
        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        g.drawCircle(cx, cy, ROSE_RADIUS);
        g.endFill();

        const x0 = Math.min(cx - ROSE_RADIUS, leverEnd, escutcheon ? cx - PLATE_WIDTH / 2 : Infinity);
        const x1 = Math.max(cx + ROSE_RADIUS, leverEnd, escutcheon ? cx + PLATE_WIDTH / 2 : -Infinity);
        const y0 = escutcheon ? plateTop : keyed ? cy - CYLINDER_ABOVE_HUB - CYLINDER_RADIUS : cy - ROSE_RADIUS;
        const y1 = escutcheon ? plateTop + PLATE_HEIGHT : cy + ROSE_RADIUS;
        return new Rectangle(x0 - HIT_PAD, y0 - HIT_PAD, x1 - x0 + 2 * HIT_PAD, y1 - y0 + 2 * HIT_PAD);
    },
};
